import {
  fork,
  put,
  call,
  delay,
  take,
  race,
  cancel,
  select,
} from "redux-saga/effects";
import { push } from "connected-react-router";
import jwtDecode from "jwt-decode";

import {
  login,
  logout,
  autoLogin,
  loginSuccess,
  loginFailure,
  setEmail,
  setAccessTokenData,
  clearAccessTokenData,
  selectIsAuthorized,
  selectRemainingTokenTimeMs,
  selectRefreshToken,
} from "../store/authSlice";

import {
  startLoading,
  setSuccess,
  setError,
  clearImpersonatedCustomer,
} from "../store/appSlice";
import { fetchCurrentUser } from "../store/usersSlice";

import {
  startNotificationsPolling,
  stopNotificationsPolling,
} from "../store/notificationsSlice";

import * as AuthApi from "../api/Auth";

export const REFRESH_TOKEN_THRESHOLD_MS = 15 * 60 * 1000;
const RETRY_DELAY_MS = 5 * 1000;
const MAX_RETRIES = 5;

export function* loginSaga({ payload } = {}) {
  const { rememberEmail, email, password } = payload;
  const operationPath = "auth.login";
  try {
    yield put(startLoading(operationPath));
    const result = yield call(AuthApi.authenticate, { email, password });
    const tokenData = prepareTokenData(result);
    yield put(setAccessTokenData(tokenData));

    yield put(fetchCurrentUser());
    yield put(startNotificationsPolling());
    yield put(loginSuccess());

    // persist email or clear it if remember isn't checked
    yield put(setEmail(rememberEmail ? email : null));
    yield put(clearImpersonatedCustomer());
    yield put(push("/"));
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
    yield put(loginFailure());
  }
}

export function* loginFlow() {
  while (true) {
    let loginTask;
    let action;
    const isAuthorized = yield select(selectIsAuthorized);
    if (!isAuthorized) {
      const loginAction = yield take(login().type);
      loginTask = yield fork(loginSaga, loginAction);

      action = yield take([loginFailure().type, loginSuccess().type]);
    } else {
      yield put(fetchCurrentUser());
      yield put(autoLogin());
      yield put(startNotificationsPolling());
    }

    if (!action || action.type !== loginFailure().type) {
      yield race({
        logout: take(logout().type),
        refresh: call(refreshTokenFlow),
      });
    } else {
      if (loginTask) {
        yield cancel(loginTask);
      }
    }

    yield put(clearImpersonatedCustomer());
    yield put(stopNotificationsPolling());
    yield put(clearAccessTokenData());
  }
}

export function* refreshTokenFlow() {
  let retries = 0;

  while (true) {
    if (retries >= MAX_RETRIES) {
      console.info("Failed to refresh the token");
      // TODO: show message on the login screen?
      return;
    }
    const tokenExpiryMs = yield select(selectRemainingTokenTimeMs);
    const refreshDelayMs = calculateRefreshDelay(tokenExpiryMs);
    if (refreshDelayMs > 0) {
      yield delay(refreshDelayMs);
    }

    try {
      yield call(refreshTokenSaga);
      retries = 0;
    } catch (error) {
      // TODO: handle revoked token (sign out early)?
      console.error("Refreshing token failed", error);
      yield delay(RETRY_DELAY_MS);
      retries += 1;
    }
  }
}

export function* refreshTokenSaga() {
  const refreshToken = yield select(selectRefreshToken);
  const result = yield call(AuthApi.refresh, { refreshToken });
  const tokenData = prepareTokenData(result);
  yield put(setAccessTokenData(tokenData));
}

function calculateRefreshDelay(remainingTokenTimeMs) {
  return Math.max(0, remainingTokenTimeMs - REFRESH_TOKEN_THRESHOLD_MS);
}

export default function* rootSaga() {
  yield fork(loginFlow);
}

export function prepareTokenData(apiResult) {
  const accessToken = apiResult.access_token;
  const tokenPayload = jwtDecode(accessToken);
  const tokenExpiryMs = apiResult.expires_in * 1000;
  return {
    accessToken,
    refreshToken: apiResult.refresh_token,
    expirationTimestamp: Date.now() + tokenExpiryMs,
    tokenExpiryMs,
    userId: tokenPayload.user_id,
    customerId: tokenPayload.customer_id,
    roles: tokenPayload.roles,
  };
}

// TODO: detect auth timeout, renew token etc
// export const checkAuthTimeout = (expirationTime) => {
//   return dispatch => {
//       setTimeout(() => {
//           dispatch(logout())
//       }, expirationTime * 1000)
//   }
// };
